/**
 * InternshipHunterTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 95 / 100 — ATS Safe
 * Style: Projects & skills-first — ideal for students with no full-time exp
 * Best For: Internship applications, hackathons, 1st/2nd year students
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, SkillsBlock,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';

function InternshipHunterTemplate({
  resumeData,
  accentColor = '#7C3AED',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, extraList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const ISection = ({ title }) => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0 6px' }}>
      <h2 style={{
        fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '1.2px', color: '#fff', margin: 0, whiteSpace: 'nowrap',
        background: accentColor, padding: '3px 10px', borderRadius: 3,
        fontFamily: 'inherit',
      }}>{title}</h2>
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: fs(DS.fs.body), lineHeight: 1.5, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Header ─── */}
      <header style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
              style={{ fontSize: fs('22px'), fontWeight: 900, color: '#0f172a', margin: 0 }} placeholder="Your Name" />
            {(role || editMode) && (
              <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
                style={{ fontSize: fs('10.5px'), fontWeight: 600, color: accentColor, margin: '2px 0 0' }} placeholder="Aspiring Software Engineer" />
            )}
          </div>
        </div>
        <div style={{ height: 2, background: `linear-gradient(90deg, ${accentColor}, transparent)`, margin: '6px 0 4px' }} />
        <ContactRow data={d} accentColor={accentColor} />
      </header>

      {/* ─── Skills FIRST (no exp → show capabilities) ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Skills" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="tags" />
        </section>
      )}

      {/* ─── Projects ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Projects" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a' }}>{proj.title || proj.name}</span>
                  {proj.tech?.length > 0 && (
                    <span style={{ fontSize: fs(DS.fs.small), color: accentColor, marginLeft: 8, fontStyle: 'italic', fontWeight: 600 }}>
                      [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                    </span>
                  )}
                </div>
                {(proj.impact || proj.status) && (
                  <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 700 }}>{proj.impact || proj.status}</span>
                )}
              </div>
              {proj.description && (
                <p style={{ fontSize: fs(DS.fs.small), color: '#6B7280', margin: '2px 0 0', paddingLeft: 10, lineHeight: 1.5 }}>{proj.description}</p>
              )}
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Internships ─── */}
      {internships.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Internship Experience" />
          {internships.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' @ '}{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Education" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', margin: 0, display: 'inline' }} />
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body), marginLeft: 6 }}>{edu?.college}</span>
                {edu?.cgpa && <span style={{ fontSize: fs(DS.fs.small), color: accentColor, fontWeight: 700, marginLeft: 6 }}>CGPA: {edu.cgpa}</span>}
              </div>
              <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
                tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap' }} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Summary ─── */}
      {summaryText && (
        <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="About" />
          <p style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0 }}>{summaryText}</p>
        </section>
      )}

      {/* ─── Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Work Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', margin: 0, display: 'inline' }} />
                  {exp.company && <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body) }}>{' — '}{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Certifications" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Achievements" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
      {extraList.length > 0 && (
        <section data-ats="extracurricular" style={{ marginBottom: DS.sp.sectionMb }}>
          <ISection title="Activities" />
          <BulletList points={extraList} accentColor={accentColor} />
        </section>
      )}
    </div>
  );
}

export default InternshipHunterTemplate;
